import { Content } from '../../types';

export const paiContent: Content = {
  title: 'פאי - העיירה הציורית בצפון תאילנד',
  imageUrl: '/images/thailand/pai/pai-wide.jpeg',
  introduction: `פאי הינה עיירה קטנה וקסומה השוכנת במחוז מה הונג סון שבצפון תאילנד, כשלוש וחצי שעות נסיעה מצ'אנג מאי. הדרך לעיירה נחשבת לאחת היפות והמרתקות ביותר בתאילנד, וכוללת לא פחות מ-800 פיתולים חדים, מה שהופך את המסע לחוויה בפני עצמה.`,
  sections: [
    {
      title: 'אווירה ייחודית ותיירות מתפתחת',
      content: `פאי ממוקמת על גדות הנהר פאי ומונה אוכלוסייה קטנה יחסית, אך למרות זאת מהווה מוקד משיכה מרכזי לתיירים, בעיקר תרמילאים המחפשים שלווה, טבע ואווירה משוחררת. בעשור האחרון הפכה העיירה ליעד מבוקש גם עבור משפחות וזוגות המחפשים חופשה רגועה באזור כפרי עם נוף עוצר נשימה.`,
      type: 'text',
    },
    {
      title: 'הרחובות המרכזיים ושוק הלילה',
      type: 'sub-headers',
      description: 'שני הרחובות המרכזיים של פאי הינם:',
      content: [
        {
          subtitle: 'Rangsiyanon Road',
          text: "הכביש הראשי המוביל לעיירה מצ'אנג מאי, שלאורכו ניתן למצוא בתי מלון, מסעדות, חנויות, בתי קפה ועוד.",
        },
        {
          subtitle: 'Chai Sangkhram Road',
          text: "הלב הפועם של חיי הלילה בעיירה. רחוב זה מהווה את ה-Walking Street של פאי ובו מרוכזים בתי הארחה, ברים, מסעדות, חנויות קעקועים, מכוני מסאג' ועוד. בכל ערב מתקיים בו שוק הלילה של פאי, המציע דוכני אוכל רחוב תאילנדי ובינלאומי, בגדים, תכשיטים ומזכרות בעבודת יד.",
        },
      ],
    },
    {
      title: 'אטרקציות ופעילויות בפאי ובסביבתה',
      type: 'sub-headers',
      description:
        'פאי וסביבתה מציעים מגוון רחב של פעילויות המתאימות לחובבי טבע, הרפתקאות ורוגע:',
      content: [
        {
          subtitle: 'טרקים ומפלים',
          text: 'סביב העיירה ניתן למצוא מסלולי הליכה מרהיבים, הכוללים ביקור במפלים כגון מפלי Mo Paeng הפופולריים.',
        },
        {
          subtitle: 'רפטינג בנהר פאי',
          text: 'חוויית שיט מהנה ורטובה המתאימה להרפתקנים.',
        },
        {
          subtitle: 'מעיינות חמים',
          text: 'פאי מציעה כמה מעיינות חמים טבעיים, ביניהם Sai Ngam Hot Springs ו-Tha Pai Hot Springs.',
        },
        {
          subtitle: 'קניון פאי (Pai Canyon)',
          text: 'נקודת תצפית מדהימה, במיוחד בשעות השקיעה.',
        },
        {
          subtitle: 'Baan Santichon',
          text: 'כפר סיני מסורתי בו ניתן להכיר את התרבות המקומית וליהנות מאוכל סיני אותנטי.',
        },
        {
          subtitle: 'מקדש Phra That Mae Yen',
          text: 'מקדש הממוקם על גבעה המשקיפה על כל אזור פאי, עם פסל בודהה לבן מרשים.',
        },
        {
          subtitle: 'גשר הזיכרון',
          text: 'גשר היסטורי מתקופת מלחמת העולם השנייה, המהווה נקודת ציון חביבה על תיירים ותושבים מקומיים.',
        },
        {
          subtitle: 'מערת לוד (Tham Lod Cave)',
          text: 'אחת המערות היפות בתאילנד, הכוללת נהר זורם ונטיפים מרהיבים. היא ממוקמת כשעה נסיעה מפאי ומהווה יעד מושלם לטיול יום.',
        },
      ],
    },
    {
      title: 'מתי מומלץ לבקר בפאי?',
      type: 'sub-headers',
      description:
        'העיירה פתוחה לביקורי תיירים במשך כל השנה, אך התקופה המומלצת ביותר לביקור היא בין נובמבר למרץ, אז הטמפרטורות נוחות והגשם מועט.',
      content: [
        {
          subtitle: 'יוני-אוקטובר',
          text: 'עונה גשומה, אך הנוף הירוק מרהיב במיוחד.',
        },
        {
          subtitle: 'אפריל-מאי',
          text: 'חם מאוד ובמהלך תקופה זו החקלאים שורפים את השדות, מה שעלול להשפיע על איכות האוויר. עם זאת, ניתן עדיין ליהנות מהעיירה.',
        },
      ],
    },
    {
      title: 'בשורה התחתונה',
      content: `בשורה התחתונה, פאי הינה עיירה ייחודית המשלבת טבע עוצר נשימה, אווירה נינוחה ושלל פעילויות לכל סוגי המטיילים. בין אם אתם תרמילאים המחפשים חוויות חדשות, זוגות המעוניינים בנופש רומנטי או משפחות הרוצות ליהנות מהשקט הכפרי של צפון תאילנד – פאי היא יעד מושלם שלא כדאי לפספס.`,
      type: 'text',
    },
  ],
};
